'use client';

import Image from "next/image";
import Link from "next/link";
import { Skeleton } from "@/components/shadcn/skeleton";
import PageLayout from "@/components/common/PageLayout";
import { Check } from "lucide-react";
import { DownloadAttachmentButton } from "@/components/common/button/DownloadAttachmentButton";
import { Dialog, DialogContent, DialogTitle } from "@/components/shadcn/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { StaticSectionProps } from "./static";
import {
  CalculatorSection,
  OrangeVisionSection,
  GrayCardGridSection,
  ChecklistContentSection,
  ImageQuotesSection,
  YouTubeVideoSection,
  TableSection,
  CardsSection,
  FeatureSection,
  BulletItemsSection,
  ItemsSection,
  ErrorSection,
  IconGridSection,
  ButtonGroupSection,
  InstagramPostSection,
  CardGridSection,
  MasonryGridSection,
  SwiperCarouselSection,
  ImageGallerySection,
  TestimonialsGrid,
  PdfViewerSection,
  AttachmentSliderSection,
  AttachmentListTableSection,
  AffiliateTableSection,
  TabSection,
  PhotoCardGridSection,
  AccordionSection,
  RoadmapRowSection,
  PartnerInformationSection,
} from "./components";
import { useEffect, useState } from "react";

export default function StaticSection({
  title,
  bannerUrl,
  query,
  skeletonCount = 2,
  withLayout = true,
  sidebarMenuId,
  sidebarParentSlug,
}: StaticSectionProps) {
  const [isPopupDialogOpen, setIsPopupDialogOpen] = useState(false);

  const popupDialogSection = query.data?.sections?.find(
    (section) => section.type === "popup_dialog"
  );
  const popupDialogData = popupDialogSection?.data;
  const popupTextContent = (popupDialogData?.content ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .trim();
  const hasPopupText = popupTextContent.length > 0;

  useEffect(() => {
    // Scroll to top when component mounts
    console.log(query);
  }, [query]);

  useEffect(() => {
    const hasPopupContent = Boolean(
      popupDialogData?.image || hasPopupText
    );
    if (popupDialogData?.is_active && hasPopupContent) {
      setIsPopupDialogOpen(true);
    }
  }, [popupDialogData?.is_active, popupDialogData?.image, hasPopupText]);
  // Use banner from data if available, otherwise use prop
  const effectiveBannerUrl = query.data?.bannerUrl || bannerUrl;
  
  const content = (
    <>
      {/* Loading */}
      {query.isLoading && (
        <div className="flex flex-col gap-8 items-start w-full">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <div key={i} className="flex flex-col md:flex-row gap-8 items-start w-full">
              <Skeleton className="w-[130px] h-[130px] rounded-xl" />
              <div className="flex flex-col gap-3 w-full">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {query.error && !query.isLoading && (
        <ErrorSection />
      )}

      {/* Data */}
      {query.data && !query.isLoading && !query.error && (
        <div className="flex flex-col gap-8 items-start w-full">
          {/* {query.data.title && query.data.titleCenter && (
            <h1 className={`text-primary-700 text-[40px] font-bold ${query.data.titleCenter ? 'self-center' : ""}`}>
              {query.data.title}
            </h1>
          )} */}
          {query.data.illustration && (
            <Image
              src={query.data.illustration}
              alt={title}
              width={0}
              height={0}
              sizes="100vw"
              className="w-full h-auto object-contain rounded-2xl"
            />
          )}

          {query.data.intro && (
            <span className="text-black font-normal text-[18px]">
              {query.data.intro}
            </span>
          )}


          {query.data.sections.map((section, idx) => {
            if (section.type === "popup_dialog") {
              return null;
            }

            const hasSeparator = section.separator;
            const isAttachmentOnly = section.action && section.action.length > 0 && !section.items && !section.cards && !section.feature && !section.table && !section.image;
            const hasBulletItems = section.bulletItems && section.bulletItems.length > 0;
            
            return (
              <div key={idx} className={`flex flex-col gap-3 w-full ${hasSeparator ? "border-b border-gray-200 pb-10" : ""
                } `}>
{section.heading && !isAttachmentOnly && !hasBulletItems && (() => {
                  const level = section.heading_level || 'h2';
                  const alignment = section.heading_alignment || 'left';
                  const alignmentClass = {
                    left: 'text-left',
                    center: 'text-center',
                    right: 'text-right'
                  }[alignment];
                  const sizeClass = {
                    h1: 'text-4xl',
                    h2: 'text-3xl',
                    h3: 'text-2xl',
                    h4: 'text-xl',
                    h5: 'text-lg',
                    h6: 'text-base'
                  }[level];
                  const className = `text-primary-700 ${sizeClass} font-bold ${alignmentClass}`;
                  
                  switch (level) {
                    case 'h1':
                      return <h1 className={className}>{section.heading}</h1>;
                    case 'h2':
                      return <h2 className={className}>{section.heading}</h2>;
                    case 'h3':
                      return <h3 className={className}>{section.heading}</h3>;
                    case 'h4':
                      return <h4 className={className}>{section.heading}</h4>;
                    case 'h5':
                      return <h5 className={className}>{section.heading}</h5>;
                    case 'h6':
                      return <h6 className={className}>{section.heading}</h6>;
                    default:
                      return <h2 className={className}>{section.heading}</h2>;
                  }
                })()}

                {section.items && (
                  <ItemsSection items={section.items} />
                )}

                {section.bulletItems && section.bulletItems.length > 0 && (
                  <BulletItemsSection 
                    bulletItems={section.bulletItems}
                    image={typeof section.image === 'string' ? section.image : section.image?.url}
                    imageAlt={section.imageAlt}
                    heading={section.heading}
                    iconColor={section.iconColor}
                  />
                )}

                {section.image && (section.imagePosition || !section.bulletItems) && !section.bulletItems && !section.video && (
                  <div className={`w-full flex ${
                    section.imagePosition === 'center' || section.imageNotFull ? "justify-center" : 
                    section.imagePosition === 'right' ? "justify-end" : 
                    "justify-start"
                  }`}>
                    <Image
                      src={typeof section.image === 'string' ? section.image : section.image.url}
                      alt={(section.imageAlt || section.heading) ?? "SMF"}
                      width={section.imageSize?.width ?? 800}
                      height={section.imageSize?.height ?? 600}
                      unoptimized
                      className={`h-auto object-contain ${section.size || ''}`}
                    />
                  </div>
                )}
                
                {/* Orange Vision Section */}
                {section.type === 'orange_vision_section' && section.data?.main_quote && (
                  <OrangeVisionSection mainQuote={section.data.main_quote} add_background={section.data.add_background} />
                )}

                {section.imageQuotes && (
                  <ImageQuotesSection 
                    imageSrc={section.imageQuotes.imageSrc}
                    quotes={section.imageQuotes.quotes || ''}
                    add_background={section.data?.add_background}
                  />
                )}

                {section.video && (
                  <YouTubeVideoSection video={section.video} add_background={section.data?.add_background} />
                )}

                {section.content && !isAttachmentOnly && (
                  section.data?.add_background ? (
                    <section
                      className="
                        w-screen relative left-1/2 right-1/2 -mx-[40vw]
                        bg-[linear-gradient(to_bottom,rgba(237,237,249,0),#DEE5E8)]
                        mb-8 py-8
                      "
                    >
                      <div className="bg-[url('/images/left-corner-supergrafis.png')] bg-no-repeat bg-left-bottom bg-[length:400px]">
                        <div className="container max-w-7xl mx-auto items-start gap-8 flex">
                          <div className="flex-1 min-w-0 px-10">
                            {typeof section.content === "string" ? (
                              section.content.trim().startsWith("<") ? (
                                <div
                                  className="
                                  text-black font-normal text-[18px] [&_p]:mb-[18px] [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_a]:text-primary-700 [&_a]:underline overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_th]:bg-primary-700 [&_th]:text-white [&_th]:p-3 [&_th]:text-left [&_th]:align-middle [&_th_p]:my-0 [&_td]:p-3 [&_td]:border [&_td]:border-gray-200 [&_td_p]:my-0 [&_tbody_tr:nth-child(even)]:bg-gray-50 [&_tbody_tr:nth-child(odd)]:bg-white"
                                  dangerouslySetInnerHTML={{ __html: section.content }}
                                />
                              ) : (
                                <p key={idx} className="text-black font-normal text-[18px]">
                                  {section.content}
                                </p>
                              )
                            ) : null}
                          </div>
                          <div className="w-80 flex-shrink-0 lg:block"></div>
                        </div>
                      </div>
                    </section>
                  ) : (
                    typeof section.content === "string" ? (
                      section.content.trim().startsWith("<") ? (
                        <div
                          className="
                          text-black font-normal text-[18px] [&_p]:mb-[18px] [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_a]:text-primary-700 [&_a]:underline overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_th]:bg-primary-700 [&_th]:text-white [&_th]:p-3 [&_th]:text-left [&_th]:align-middle [&_th_p]:my-0 [&_td]:p-3 [&_td]:border [&_td]:border-gray-200 [&_td_p]:my-0 [&_tbody_tr:nth-child(even)]:bg-gray-50 [&_tbody_tr:nth-child(odd)]:bg-white"
                          dangerouslySetInnerHTML={{ __html: section.content }}
                        />
                      ) : (
                        <p key={idx} className="text-black font-normal text-[18px]">
                          {section.content}
                        </p>
                      )
                    ) : null
                  )
                )}

                {section.image && !section.imagePosition && (
                  <div className={`w-full flex ${section.imageNotFull ? "justify-center" : ""}`}>
                    <Image
                      src={typeof section.image === 'string' ? section.image : section.image.url}
                      alt={section.heading ?? "SMF"}
                      width={section.imageSize?.width ?? 800}
                      height={section.imageSize?.height ?? 600}
                      unoptimized
                      className={`h-auto object-contain ${section.size || ''}`}
                    />
                  </div>
                )}

                {section.table && (
                  <TableSection table={section.table} />
                )}

                {section.action && section.action.length > 0 && (
                  <div className="flex flex-col gap-4">
                    {section.action.map((action, i) => {
                      // Handle both string and object formats for url
                      const urlData = action.url?.[0];
                      let fileUrl = '#';
                      
                      if (typeof urlData === 'string') {
                        fileUrl = urlData;
                      } else if (urlData && typeof urlData === 'object' && 'url' in urlData) {
                        fileUrl = String((urlData as { url: string }).url);
                      }
                      
                      const isExternalUrl = fileUrl.startsWith('http');
                      
                      // Truncate title to 100 characters with ellipsis
                      const buttonTitle = action.title || 'Download';
                      
                      return (
                        <a
                          key={i}
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block w-fit"
                        >
                          <DownloadAttachmentButton
                            className="w-fit bg-primary-700 text-white font-bold h-auto py-4 px-5 text-left whitespace-normal break-words"
                          >
                            {buttonTitle}
                          </DownloadAttachmentButton>
                        </a>
                      );
                    })}
                  </div>
                )}


                {section.cards && section.cards.length > 0 && (
                  <CardsSection cards={section.cards} />
                )}

                {Array.isArray(section.feature) && section.feature.length > 0 && (
                  <FeatureSection features={section.feature} featuresLength={section.feature.length} />
                )}

                {/* Gray Card Grid Section */}
                {section.type === 'gray_card_grid_section' && section.data?.cards && (
                  <GrayCardGridSection 
                    cards={section.data.cards.filter((card): card is { icon: string; description: string } => 
                      typeof card.description === 'string' && card.description.length > 0
                    )}
                    columns={section.data.columns}
                    add_background={section.data.add_background}
                  />
                )}

                {/* Checklist Content Section */}
                {section.type === 'checklist_content' && section.data && (
                  <ChecklistContentSection 
                    content={section.data.content}
                    list={section.data.list || []}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Calculator Section */}
                {section.type === 'calculator_section' && section.data?.enable_calculator && (
                  <CalculatorSection heading={section.data.heading} add_background={section.data.add_background} />
                )}

                {/* Icon Grid Section */}
                {section.type === 'icon_grid_section' && section.data?.grid_items && (
                  <IconGridSection 
                    heading={section.data.heading}
                    grid_items={section.data.grid_items}
                    columns={section.data.columns}
                    add_background={section.data.add_background}
                  />
                )}

                {/* Button Group Section */}
                {section.type === 'button_group_section' && section.data?.buttons && (
                  <ButtonGroupSection 
                    heading={section.data.heading}
                    buttons={section.data.buttons}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Instagram Post Section */}
                {section.type === 'instagram_post_section' && section.data && (
                  <InstagramPostSection 
                    heading={section.data.heading}
                    enable_instagram_feed={section.data.enable_instagram_feed}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Card Grid Section */}
                {section.type === 'card_grid_section' && section.data?.cards && (
                  <CardGridSection 
                    heading={section.data.heading}
                    cards={section.data.cards}
                    columns={section.data.columns}
                    card_style={section.data.card_style as "default" | "bordered" | "elevated" | undefined}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Masonry Grid Section */}
                {section.type === 'masonry_grid_section' && section.data?.items && (
                  <MasonryGridSection 
                    heading={section.data.heading}
                    items={section.data.items}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Swiper Carousel Section */}
                {section.type === 'swiper_carousel_section' && section.data?.items && (
                  <SwiperCarouselSection 
                    heading={section.data.heading}
                    items={section.data.items.filter((item): item is { image: string; title: string; description: string } => 
                      typeof item.image === 'string' && 
                      typeof item.title === 'string' && 
                      typeof item.description === 'string'
                    )}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Image Gallery Section */}
                {section.type === 'image_gallery_section' && section.data?.images && (
                  <ImageGallerySection 
                    heading={section.data.heading}
                    images={section.data.images}
                    columns={section.data.columns as 2 | 3 | 4 | undefined}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Testimonials Grid Section */}
                {section.type === 'testimonials_grid' && section.data?.testimonials && (
                  <TestimonialsGrid 
                    testimonials={section.data.testimonials}
                    grid_columns={section.data.grid_columns}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* PDF Viewer Section */}
                {section.type === 'pdf_viewer_section' && section.data?.pdf_file && (
                  <PdfViewerSection 
                    title={section.data.title}
                    pdf_file={section.data.pdf_file}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Attachment Slider Section */}
                {section.type === 'attachment_slider' && section.data?.attachment_slider_items && (
                  <AttachmentSliderSection
                    heading={section.data.heading}
                    description={section.data.description}
                    items={section.data.attachment_slider_items}
                    visibleItems={section.data.visible_items}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Photo Card Grid Section */}
                {section.type === 'photo_card_grid_section' && section.data?.photo_cards && (
                  <PhotoCardGridSection
                    heading={section.data.heading}
                    cards={section.data.photo_cards}
                    columns={section.data.columns}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Accordion Section */}
                {section.type === 'accordion_section' && section.data?.accordion_items && (
                  <AccordionSection
                    heading={section.data.heading}
                    items={section.data.accordion_items}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Roadmap Row Section */}
                {section.type === 'roadmap_row_section' && section.data?.roadmap_items && (
                  <RoadmapRowSection
                    items={section.data.roadmap_items}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Partner Information Section */}
                {section.type === 'partner_information_section' && section.data?.partner_items && (
                  <PartnerInformationSection
                    heading={section.data.heading}
                    items={section.data.partner_items}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Tab Section */}
                {section.type === 'tab_section' && section.data?.tab_items && (
                  <TabSection
                    heading={section.data.heading}
                    tab_items={section.data.tab_items}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {/* Attachment List Table Section */}
                {section.type === 'attachment_list_table' && section.data?.attachment_list_table_items && (
                  <AttachmentListTableSection
                    heading={section.data.heading}
                    items={section.data.attachment_list_table_items}
                    show_category_column={section.data.show_category_column}
                    show_year_column={section.data.show_year_column}
                    show_file_size_column={section.data.show_file_size_column}
                    striped={section.data.striped}
                    bordered={section.data.bordered}
                    add_background={section.data.add_background}
                    add_bottom_border={section.data.add_bottom_border}
                  />
                )}

                {section.type === 'affiliate_table' && section.data?.is_published !== false && (
                  <AffiliateTableSection />
                )}

                {section.checklist && section.checklist.length > 0 && (
                  <ul className="list-none space-y-2">
                    {section.checklist.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="text-primary-300 text-xl shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {Array.isArray(section.detailList) && section.detailList.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
                    {section.detailList.map((item, i) => {
                      return (
                        <div
                          key={i}
                          className="flex flex-col md:col-span-6 lg:col-span-3"
                        >
                          <p className="text-primary-700 text-2xl font-bold">{item.title}</p>
                          {item.description && (
                            <p className="text-gray-700 text-[18px]" dangerouslySetInnerHTML={{ __html: item.description }}></p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Empty */}
      {!query.isLoading && !query.error && !query.data && (
        <p className="text-gray-500">No data available.</p>
      )}

      <Dialog open={isPopupDialogOpen} onOpenChange={setIsPopupDialogOpen}>
        <DialogContent className="sm:max-w-xl p-0 overflow-hidden">
          <VisuallyHidden><DialogTitle>Popup</DialogTitle></VisuallyHidden>
          {popupDialogData?.image && (
            <img
              src={popupDialogData.image}
              alt={popupDialogData.image_alt ?? "popup image"}
              className="w-full h-auto object-cover"
            />
          )}
          {hasPopupText && (
            <div
              className="prose max-w-none typography p-6"
              dangerouslySetInnerHTML={{ __html: popupDialogData?.content ?? "" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  // If error, don't use layout (no banner)
  if (query.error && !query.isLoading) {
    return content;
  }

  return withLayout ? (
    <PageLayout 
      title={title} 
      bannerUrl={effectiveBannerUrl}
      navigation={query.data?.navigation}
      useDynamicBreadcrumbs={!!query.data?.navigation}
      sidebarMenuId={sidebarMenuId}
      sidebarParentSlug={sidebarParentSlug}
    >
      {content}
    </PageLayout>
  ) : (
    content
  );
}
